"use client";

import { Label } from "@/components/ui/label";
import { PrerequisiteSelector } from "./PrerequisiteSelector";
import type { TemplateInfo, CampaignPrerequisiteConfig } from "@/lib/claims/eligibility-types";
import { cn } from "@/lib/utils";

export interface CampaignPrerequisitesProps {
  /** Current prerequisite configuration */
  config: CampaignPrerequisiteConfig;
  /** Callback when configuration changes */
  onConfigChange: (config: CampaignPrerequisiteConfig) => void;
  /** Available templates to select from */
  availableTemplates: TemplateInfo[];
  /** Optional className for styling */
  className?: string;
}

/**
 * CampaignPrerequisites Component
 *
 * Allows campaign-level prerequisite configuration with:
 * - Enable/disable toggle for campaign-wide prerequisites
 * - Embedded PrerequisiteSelector when enabled
 * - Helper text explaining how campaign-level prerequisites work
 */
export function CampaignPrerequisites({
  config,
  onConfigChange,
  availableTemplates,
  className,
}: CampaignPrerequisitesProps) {
  const handleEnableChange = (enabled: boolean) => {
    onConfigChange({
      ...config,
      enabled,
      // Clear templateIds if disabling
      templateIds: enabled ? config.templateIds : [],
    });
  };

  const handleSelectionChange = (templateIds: string[]) => {
    onConfigChange({
      ...config,
      templateIds,
    });
  };

  const handleLogicTypeChange = (logicType: "AND" | "OR") => {
    onConfigChange({
      ...config,
      logicType,
    });
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Section Header */}
      <div className="border-b border-gray-200 pb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Campaign Prerequisites
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Configure NFT requirements that apply to all locations in this campaign by default.
        </p>
      </div>

      {/* Enable/Disable Toggle */}
      <div className="flex items-start gap-3">
        <div className="flex h-6 items-center">
          <input
            id="campaign-prerequisites-enabled"
            type="checkbox"
            checked={config.enabled}
            onChange={(e) => handleEnableChange(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
        </div>
        <div className="flex-1">
          <Label
            htmlFor="campaign-prerequisites-enabled"
            className="text-sm font-medium text-gray-900"
          >
            Enable campaign-level prerequisites
          </Label>
          <p className="mt-1 text-sm text-gray-500">
            When enabled, participants must own the specified NFTs before they can claim
            rewards at any location in this campaign. Individual locations can override
            these settings.
          </p>
        </div>
      </div>

      {/* Prerequisite Selector (shown when enabled) */}
      {config.enabled && (
        <div className="ml-7 border-l-2 border-blue-200 pl-4">
          <PrerequisiteSelector
            selectedTemplates={config.templateIds}
            onSelectionChange={handleSelectionChange}
            logicType={config.logicType}
            onLogicTypeChange={handleLogicTypeChange}
            availableTemplates={availableTemplates}
          />

          {/* Info Box */}
          <div className="mt-4 rounded-md bg-blue-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-blue-400"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a.75.75 0 000 1.5h.253a.25.25 0 01.244.304l-.459 2.066A1.75 1.75 0 0010.747 15H11a.75.75 0 000-1.5h-.253a.25.25 0 01-.244-.304l.459-2.066A1.75 1.75 0 009.253 9H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-blue-800">
                  How Campaign Prerequisites Work
                </h4>
                <div className="mt-2 text-sm text-blue-700">
                  <ul className="list-disc space-y-1 pl-5">
                    <li>
                      These prerequisites apply to <strong>all locations</strong> in this campaign.
                    </li>
                    <li>
                      Individual locations can <strong>override</strong> these settings with their own prerequisites.
                    </li>
                    <li>
                      Participants must own the required NFTs at the <strong>time of claiming</strong>.
                    </li>
                    <li>
                      Use this for hunts where earlier NFTs unlock later rewards.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disabled State Info */}
      {!config.enabled && (
        <div className="ml-7 rounded-md bg-gray-50 p-4">
          <p className="text-sm text-gray-600">
            Campaign-level prerequisites are disabled. Each location will use its own
            prerequisite settings (if configured).
          </p>
        </div>
      )}
    </div>
  );
}
